import { Link } from "react-router-dom";

/**
 * Route-aware link helpers.
 *
 * Content links (hero CTAs, section cards, etc.) carry hrefs from editable
 * page content — internal app paths look like "/catalog?category=Footwear".
 * Rendered as a plain <a>, those trigger a full browser navigation, which
 * breaks the static (S3) build: it uses HashRouter, so routes live under
 * "#/…" and a hard nav to "/catalog?…" hits a non-existent S3 object (403).
 *
 * Routing internal paths through react-router's <Link> lets the active router
 * (Hash or Browser) build the correct href and navigate client-side, so the
 * same content data works in both build modes. External/hash/mailto/tel links
 * stay plain anchors.
 */

// Internal app route: absolute path like "/catalog", but not protocol-relative
// "//host", in-page "#anchor", or an external/scheme URL.
export function isInternalPath(href) {
  return (
    typeof href === "string" && href.startsWith("/") && !href.startsWith("//")
  );
}

/**
 * Anchor that routes internal paths via <Link> and renders everything else as
 * a plain <a>. Pass-through props (className, onClick, aria-*) apply to both.
 */
export function SmartLink({ href, children, ...rest }) {
  if (isInternalPath(href)) {
    return (
      <Link to={href} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

/**
 * Props for components that accept react-bootstrap's `as`/`to` (e.g. Button):
 * routes internal paths through <Link>, otherwise sets a plain href (falling
 * back to `fallback` when href is empty).
 */
export function linkProps(href, { fallback } = {}) {
  if (isInternalPath(href)) return { as: Link, to: href };
  return { href: href || fallback };
}
